import { Event } from '../types/event.types';

// Types pour nos règles
interface SortingRule {
  name: string;
  weight: number;
  calculate: (event: Event, userLocation?: { latitude: number; longitude: number }) => number;
}

interface ViewConfig {
  rules: SortingRule[];
  options: {
    maxDistance: number; 
    userMaxDistance?: number;    // en km
    maxEvents: number;      // nombre max d'événements
    maxDays: number;        // horizon temporel en jours
  };
}

// Fonctions utilitaires
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Rayon de la Terre en km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Fonction pour formater la distance avec des descriptions contextuelles
export const formatDistance = (distance: number): string => {
  if (distance < 0.1) {
    return ' • < 100m';
  } else if (distance < 1) {
    return ` • ${(distance * 1000).toFixed(0)}m`;
  } else {
    return ` • ${distance.toFixed(1)}km`;
  }
};

// Fonction pour ouvrir dans Maps
export const openInMaps = (latitude: number, longitude: number, address: string) => {
  const encodedAddress = encodeURIComponent(address);

  // iOS: open Apple Maps natively
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    window.location.href = `maps://maps.apple.com/?q=${encodedAddress}&ll=${latitude},${longitude}`;
    return;
  }

  // Everything else (Android, desktop): Google Maps URL
  // Works everywhere — on Android it opens Google Maps app if installed
  // Avoids geo: scheme error on desktop/emulators
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  window.open(googleMapsUrl, '_blank');
};

// Configuration pour chaque vue
export const viewConfigs: Record<'list' | 'map', ViewConfig> = {
  list: {
    rules: [
      {
        name: 'distance',
        weight: 0.4,
        calculate: (event, userLocation) => {
          if (!userLocation || !event.latitude || !event.longitude) return 0;
          const distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            event.latitude,
            event.longitude
          );
          return Math.max(0, 1 - distance / 10000); // Score inversement proportionnel à la distance
        }
      },
      {
        name: 'time',
        weight: 0.4,
        calculate: (event) => {
          if (!event.datetime) return 0;
          const now = new Date();
          const eventDate = new Date(event.datetime);
          const daysUntil = (eventDate.getTime() - now.getTime()) / (1000 * 3600 * 24);
          return Math.max(0, 1 - daysUntil / 10); // Priorité aux événements proches dans le temps
        }
      },
      {
        name: 'popularity',
        weight: 0.2,
        calculate: (event) => {
          return Math.min(1, (event.views || 0) / 100); // Score basé sur les vues
        }
      }
    ],
    options: {
      maxDistance: 5,     // Aligné avec FilterDrawer
      maxEvents: 50,
      maxDays: 30
    }
  },
  map: {
    rules: [
      {
        name: 'distance',
        weight: 0.6,
        calculate: (event, userLocation) => {
          if (!userLocation || !event.latitude || !event.longitude) return 0;
          const distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            event.latitude,
            event.longitude
          );
          return Math.max(0, 1 - distance / 10000); 
        }
      },
      {
        name: 'time',
        weight: 0.4,
        calculate: (event) => {
          if (!event.datetime) return 0;
          const now = new Date();
          const eventDate = new Date(event.datetime);
          const daysUntil = (eventDate.getTime() - now.getTime()) / (1000 * 3600 * 24);
          return Math.max(0, 1 - daysUntil / 7);
        }
      }
    ],
    options: {
      maxDistance: 5,     // Aligné avec FilterDrawer
      maxEvents: 30,
      maxDays: 30
    }
  }
};

// Diversité d'organisateurs : réarrange la liste pour éviter les séquences
// du même orga. Garantit au moins `minGap` orgas différents entre deux events
// du même organisateur. Si pas assez d'orgas différents, fallback naturel.
export const interleaveByOrganizer = (events: Event[], minGap: number = 3): Event[] => {
  if (events.length <= minGap) return events;

  // Récupérer l'orgId de façon safe (undefined/null/vide = id unique par event)
  const getOrgKey = (e: Event): string => e.organizerId || `__no_org_${e.id}`;

  // Vérifier qu'il y a au moins 2 orgas différents, sinon inutile
  const uniqueOrgs = new Set(events.map(getOrgKey));
  if (uniqueOrgs.size <= 1) return events;

  const result: Event[] = [];
  const remaining = [...events];

  while (remaining.length > 0) {
    const recentOrgs = result.slice(-minGap).map(getOrgKey);
    const nextIndex = remaining.findIndex(e => !recentOrgs.includes(getOrgKey(e)));

    if (nextIndex === -1) {
      // Plus d'orga différent dispo → prendre le premier (garde l'ordre par score/date)
      result.push(remaining.shift()!);
    } else {
      result.push(...remaining.splice(nextIndex, 1));
    }
  }

  return result;
};

// Fonction principale de tri
// 🎯 PRD HARMONISATION : Ajout paramètre hasCustomDateFilter pour étendre maxDays
export const sortEvents = (
  events: Event[],
  viewMode: 'list' | 'map',
  userLocation?: { latitude: number; longitude: number },
  userMaxDistance?: number,
  hasCustomDateFilter?: boolean // 🎯 Si true, permet d'afficher des events à +30 jours
): Event[] => {
  const config = viewConfigs[viewMode];
  // 🎯 PRD HARMONISATION : Si filtre date custom actif, étendre l'horizon à 365 jours
  const effectiveMaxDays = hasCustomDateFilter ? 365 : config.options.maxDays;

  let _filteredInvalid = 0;
  let _filteredTooFar = 0;
  let _filteredDistance = 0;

  const sorted = events
    .filter(event => {
      // ✅ Accepter les Date ET les Timestamps Firestore
      const hasValidDatetime = event.datetime && (
        event.datetime instanceof Date ||
        (typeof event.datetime === 'object' && 'seconds' in event.datetime)
      );

      if (!hasValidDatetime || !event.latitude || !event.longitude) {
        _filteredInvalid++;
        return false;
      }

      if (userLocation) {
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          event.latitude,
          event.longitude
        );
        if (viewMode === 'list' && userMaxDistance && distance > userMaxDistance) {
          _filteredDistance++;
          return false;
      }
    }

      const now = new Date();

      // ✅ Convertir datetime en Date, même si c'est un Timestamp Firestore
      let eventDate: Date;
      if (event.datetime instanceof Date) {
        eventDate = event.datetime;
      } else if (typeof event.datetime === 'object' && 'seconds' in event.datetime) {
        eventDate = new Date((event.datetime as any).seconds * 1000);
      } else if (event.datetime) {
        eventDate = new Date(event.datetime);
      } else {
        eventDate = new Date();
      }

      const daysUntil = (eventDate.getTime() - now.getTime()) / (1000 * 3600 * 24);

      // 🎯 PRD HARMONISATION : Utilise effectiveMaxDays (365 si filtre date custom)
      if (daysUntil > effectiveMaxDays) {
        _filteredTooFar++;
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      let scoreA = 0;
      let scoreB = 0;

      config.rules.forEach(rule => {
        scoreA += rule.calculate(a, userLocation) * rule.weight;
        scoreB += rule.calculate(b, userLocation) * rule.weight;
      });

      return scoreB - scoreA;
    });

  // Log résumé unique
  const totalFiltered = _filteredInvalid + _filteredTooFar + _filteredDistance;
  if (totalFiltered > 0) {
    console.log(`📊 [sortEvents] ${events.length} events → ${sorted.length} retenus (${_filteredInvalid} invalides, ${_filteredTooFar} trop loin, ${_filteredDistance} hors distance)`);
  }

  // Diversité d'organisateurs en vue liste uniquement
  const diversified = viewMode === 'list' ? interleaveByOrganizer(sorted) : sorted;

  return diversified.slice(0, config.options.maxEvents);
};
